/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 23/11/21
 */

import * as Joi from 'joi';
import { ValidationResult } from 'joi';
import { joinPaths } from '@/utils/joinPaths';
import { setConfig } from '@/wpt-lib/utils/config';

interface EnvConfig {
    REACT_APP_API_BASE_URL: string;
    REACT_APP_PDF_PROGRESS_POLLING: number;
    REACT_APP_FEATURE_FLAG_SHOW_SUMMARY_PLACEHOLDER: boolean;
    REACT_APP_FEATURE_FLAG_ENABLE_REASON_TEXT_BOX: boolean;
    REACT_APP_FEATURE_FLAG_DO_REFRESH_SUPERVISES: boolean;
    REACT_APP_FEATURE_FLAG_PDF_CELL_METHOD: 'none' | 'lines' | 'div';
    REACT_APP_AI_ASSISTANT_URL?: string;
}
const schema = Joi.object<EnvConfig, true>({
    REACT_APP_API_BASE_URL: Joi.string().uri({ allowRelative: false }).required(),
    REACT_APP_PDF_PROGRESS_POLLING: Joi.number().default(30000),
    REACT_APP_FEATURE_FLAG_SHOW_SUMMARY_PLACEHOLDER: Joi.boolean().default(false),
    REACT_APP_FEATURE_FLAG_ENABLE_REASON_TEXT_BOX: Joi.boolean().default(false),
    REACT_APP_FEATURE_FLAG_DO_REFRESH_SUPERVISES: Joi.boolean().default(false),
    REACT_APP_FEATURE_FLAG_PDF_CELL_METHOD: Joi.string().valid('none', 'lines', 'div').default('lines'),
    REACT_APP_AI_ASSISTANT_URL: Joi.string().uri({ allowRelative: false }),
}).unknown(true);

const { value: validatedEnv, error } = schema.validate(process.env, {
    abortEarly: false,
}) as Omit<ValidationResult, 'value'> & { value: EnvConfig };
if (error) {
    throw new Error(`Env config error: ${error.message}`);
}

const config = {
    auth: {
        loginUrl: joinPaths(validatedEnv.REACT_APP_API_BASE_URL, 'auth/saml/login'),
        accessTokenStorageKey: 'wpt_accessToken',
        assignmentStorageKey: 'wpt_currentAssignment',
    },
    api: {
        baseUrl: validatedEnv.REACT_APP_API_BASE_URL,
    },
    admin: {
        pdfPolling: validatedEnv.REACT_APP_PDF_PROGRESS_POLLING,
    },
    constants: {
        fullTimeHours: 1610,
        plannableFutureYears: 3,
        mainContent: 'main_content',
    },
    featureFlags: {
        showPlaceholderSummary: validatedEnv.REACT_APP_FEATURE_FLAG_SHOW_SUMMARY_PLACEHOLDER,
        enableReasonTextBox: validatedEnv.REACT_APP_FEATURE_FLAG_ENABLE_REASON_TEXT_BOX,
        doRefreshSupervises: validatedEnv.REACT_APP_FEATURE_FLAG_DO_REFRESH_SUPERVISES,
        pdfCellMethod: validatedEnv.REACT_APP_FEATURE_FLAG_PDF_CELL_METHOD,
    },
    urls: {
        aiAssistant: validatedEnv.REACT_APP_AI_ASSISTANT_URL,
    },
};

setConfig(config);

export default config;
